/**
 * Tema del sistema: UN sistema, DOS temas (.sk-despacho / .sk-taller), el
 * Taller con variante clara (.sk-light) y un «override de marca» opcional
 * (.sk-brand-<nombre>) para comparar paletas/tipografías alternativas sin
 * tocar componentes: cada marca es un bloque de tokens en tokens.css.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export type ThemeName = "despacho" | "taller";
export interface ThemeState {
  theme: ThemeName;
  light: boolean;
  brand: string | null;
  /** La marca la eligió la PERSONA (Preferencias), no el despliegue ni la casa:
      a partir de ahí manda ella, también tras recargar. */
  brandChosen: boolean;
}
interface ThemeApi extends ThemeState { toggleLight(): void; setBrand(brand: string | null, chosen?: boolean): void }

const Ctx = createContext<ThemeApi | null>(null);
const KEY = "skein-theme";

function read(fallback: ThemeState): ThemeState {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? { ...fallback, ...(JSON.parse(raw) as Partial<ThemeState>) } : fallback;
  } catch {
    return fallback;
  }
}

export function ThemeProvider({ theme, children }: { theme: ThemeName; children: ReactNode }) {
  const [state, setState] = useState<ThemeState>(() => read({ theme, light: false, brand: null, brandChosen: false }));
  useEffect(() => {
    const b = document.body;
    b.classList.add("sk-app", `sk-${state.theme}`);
    b.classList.toggle("sk-light", state.light);
    for (const c of [...b.classList]) if (c.startsWith("sk-brand-")) b.classList.remove(c);
    if (state.brand) b.classList.add(`sk-brand-${state.brand}`);
    try { localStorage.setItem(KEY, JSON.stringify({ light: state.light, brand: state.brand, brandChosen: state.brandChosen })); } catch { /* sin storage */ }
  }, [state]);
  const toggleLight = useCallback(() => setState((s) => ({ ...s, light: !s.light })), []);
  const setBrand = useCallback((brand: string | null, chosen = false) => setState((s) => ({ ...s, brand, brandChosen: s.brandChosen || chosen })), []);
  const api = useMemo<ThemeApi>(() => ({ ...state, toggleLight, setBrand }), [state, toggleLight, setBrand]);
  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}

export function useTheme(): ThemeApi {
  const v = useContext(Ctx);
  if (!v) throw new Error("useTheme fuera de <ThemeProvider>");
  return v;
}
